package data.local

import com.kinoshazam.FAVORITES_LOCAL
import com.kinoshazam.data.Storage
import data.db.delight.FavoriteVideo as FavoriteVideoRow
import data.db.delight.daos.FavoriteVideoDao
import features.videoDetails.data.model.FavoriteEntity
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import models.FavoriteVideo
import utils.toBoolean

class DelightLocalRepository(
    val favoriteVideoDao: FavoriteVideoDao,
    private val storage: Storage,
) : LocalRepository {

    private val json = Json { ignoreUnknownKeys = true }
    private val gate = Mutex()
    private var hydrated = false

    override suspend fun addFavorite(favoriteVideo: FavoriteVideo) {
        gate.withLock {
            hydrateLocked()
            favoriteVideoDao.saveFavoriteVideo(favoriteVideo)
            persistLocked()
        }
    }

    override suspend fun deleteFavorite(video: FavoriteVideo) {
        gate.withLock {
            hydrateLocked()
            favoriteVideoDao.deleteFavouriteVideo(video)
            persistLocked()
        }
    }

    override suspend fun getAllFavoriteList(withDeleted: Boolean): Flow<List<FavoriteEntity>> {
        gate.withLock { hydrateLocked() }
        val rows = favoriteVideoDao.getAllFavoriteVideos() ?: return flowOf(emptyList())
        return rows.map { item ->
            item
                .filter { withDeleted || !it.isDeleted.toBoolean() }
                .map { it.toEntity() }
        }
    }

    override suspend fun addAllFavorite(result: List<FavoriteEntity>) {
        gate.withLock {
            hydrated = true
            favoriteVideoDao.replaceAll(result)
            persistLocked()
        }
    }

    suspend fun clearFavorites() {
        gate.withLock {
            hydrated = true
            favoriteVideoDao.deleteAll()
            storage.remove(FAVORITES_LOCAL)
        }
    }

    suspend fun isFavorite(videoId: Int): Boolean {
        gate.withLock { hydrateLocked() }
        return favoriteVideoDao.isVideoFavorite(videoId = videoId).toBoolean()
    }

    private suspend fun hydrateLocked() {
        if (hydrated) return
        val existing = favoriteVideoDao.snapshot() ?: return
        hydrated = true
        if (existing.isNotEmpty()) {
            persistRows(existing)
            return
        }
        val saved = readStored()
        if (saved.isEmpty()) return
        favoriteVideoDao.saveAllFavoriteVideo(saved)
    }

    private suspend fun persistLocked() {
        val rows = favoriteVideoDao.snapshot() ?: return
        persistRows(rows)
    }

    private fun persistRows(rows: List<FavoriteVideoRow>) {
        storage.put(FAVORITES_LOCAL, json.encodeToString(rows.map { it.toEntity() }))
    }

    private fun readStored(): List<FavoriteEntity> {
        val raw = storage.get<String>(FAVORITES_LOCAL)?.takeIf { it.isNotBlank() } ?: return emptyList()
        return runCatching { json.decodeFromString<List<FavoriteEntity>>(raw) }.getOrDefault(emptyList())
    }
}

private fun FavoriteVideoRow.toEntity(): FavoriteEntity = FavoriteEntity(
    videoId = id.toString(),
    title = title,
    imageUrl = imageUrl,
    isTvShow = isTvShow.toBoolean(),
    isDeleted = isDeleted.toBoolean(),
    changedAt = changedAt.orEmpty(),
)
